import { X as CloseX } from '@untitledui/icons'

import Flag from '@components/Flag.tsx'
import { Dot } from '@components/designSystem/DotIcon'
import { cx } from '@utils/cx'

import { badgeTypes, type FlagTypes } from './badge-types'

import type { BadgeColors, BadgeTypeToColorMap, BadgeTypes, IconComponentType, Sizes } from './badge-types'
import type { MouseEventHandler, ReactNode } from 'react'

const filledColors: Record<BadgeColors, { root: string; addon: string; addonButton: string }> = {
  gray: {
    root: 'bg-utility-gray-100 text-utility-gray-600 ring-utility-gray-300',
    addon: 'text-utility-gray-600',
    addonButton: 'hover:bg-utility-gray-100 text-utility-gray-400 hover:text-utility-gray-500',
  },
  brand: {
    root: 'bg-utility-brand-50 text-utility-brand-700 ring-utility-brand-200',
    addon: 'text-utility-brand-500',
    addonButton: 'hover:bg-utility-brand-100 text-utility-brand-400 hover:text-utility-brand-500',
  },
  error: {
    root: 'bg-utility-error-50 text-utility-error-700 ring-utility-error-200',
    addon: 'text-utility-error-500',
    addonButton: 'hover:bg-utility-error-100 text-utility-error-400 hover:text-utility-error-500',
  },
  warning: {
    root: 'bg-utility-warning-50 text-utility-warning-700 ring-utility-warning-200',
    addon: 'text-utility-warning-500',
    addonButton: 'hover:bg-utility-warning-100 text-utility-warning-400 hover:text-utility-warning-500',
  },
  success: {
    root: 'bg-utility-success-50 text-utility-success-700 ring-utility-success-200',
    addon: 'text-utility-success-500',
    addonButton: 'hover:bg-utility-success-100 text-utility-success-400 hover:text-utility-success-500',
  },
  outOfShelf: {
    root: 'bg-utility-orange-dark-50 text-utility-gray-600 ring-utility-orange-dark-100',
    addon: 'text-utility-orange-dark-400',
    addonButton: 'hover:bg-utility-orange-dark-100 text-utility-orange-dark-400 hover:text-utility-orange-dark-500',
  },
  dark: {
    root: 'bg-primary-solid text-white ring-bg-primary-solid',
    addon: 'text-white/70',
    addonButton: 'hover:bg-secondary-solid text-white/60 hover:text-white',
  },
  light: {
    root: 'bg-white text-text-primary ring-border-secondary',
    addon: 'text-text-secondary',
    addonButton: 'hover:bg-bg-tertiary text-text-secondary hover:text-text-primary',
  },
}

const withPillTypes = {
  [badgeTypes.pillColor]: {
    common: 'size-max flex items-center whitespace-nowrap rounded-full ring-1 ring-inset',
    styles: filledColors,
  },
  [badgeTypes.badgeColor]: {
    common: 'size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset',
    styles: filledColors,
  },
}

const withBadgeTypes = {
  [badgeTypes.pillColor]: {
    common: 'size-max flex items-center whitespace-nowrap rounded-full ring-1 ring-inset',
    styles: filledColors,
  },
  [badgeTypes.badgeColor]: {
    common: 'size-max flex items-center whitespace-nowrap rounded-md ring-1 ring-inset',
    styles: filledColors,
  },
}

export type BadgeColor<T extends BadgeTypes> = BadgeTypeToColorMap<typeof withPillTypes>[T];

interface BadgeProps<T extends BadgeTypes> {
    type?: T
    size?: Sizes
    color?: BadgeColor<T>
    children: ReactNode
    className?: string
}

export const Badge = <T extends BadgeTypes>(props: BadgeProps<T>) => {
  const { type = 'pill-color', size = 'md', color = 'gray', children } = props
  const colors = withPillTypes[type]

  const pillSizes = {
    sm: 'py-0.5 px-2 text-xs font-regular',
    md: 'py-0.5 px-2.5 text-sm font-medium',
    lg: 'py-1 px-3 text-sm font-medium',
    xl: 'py-1.5 px-4 text-md font-medium',
  }
  const badgeSizes = {
    sm: 'py-0.5 px-1.5 text-xs font-regular',
    md: 'py-0.5 px-2 text-sm font-medium',
    lg: 'py-1 px-2.5 text-sm font-medium rounded-lg',
    xl: 'py-1.5 px-3.5 text-md font-medium rounded-lg',
  }

  const sizes = {
    [badgeTypes.pillColor]: pillSizes,
    [badgeTypes.badgeColor]: badgeSizes,
  }

  return <span className={cx(colors.common, sizes[type][size], colors.styles[color].root, props.className)}>{children}</span>
}

interface BadgeWithDotProps<T extends BadgeTypes> {
    type?: T
    size?: Sizes
    color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T]
    className?: string
    children: ReactNode
}

export const BadgeWithDot = <T extends BadgeTypes>(props: BadgeWithDotProps<T>) => {
  const { size = 'md', color = 'gray', type = 'pill-color', className, children } = props

  const colors = withBadgeTypes[type]

  const pillSizes = {
    sm: 'gap-1 py-0.5 pl-1.5 pr-2 text-xs font-regular',
    md: 'gap-1.5 py-0.5 pl-2 pr-2.5 text-sm font-medium',
    lg: 'gap-1.5 py-1 pl-2.5 pr-3 text-sm font-medium',
    xl: 'gap-2 py-1.5 pl-3.5 pr-4 text-md font-medium',
  }

  const badgeSizes = {
    sm: 'gap-1 py-0.5 px-1.5 text-xs font-regular',
    md: 'gap-1.5 py-0.5 px-2 text-sm font-medium',
    lg: 'gap-1.5 py-1 px-2.5 text-sm font-medium rounded-lg',
    xl: 'gap-2 py-1.5 px-3.5 text-md font-medium rounded-lg',
  }

  const sizes = {
    [badgeTypes.pillColor]: pillSizes,
    [badgeTypes.badgeColor]: badgeSizes,
  }

  return (
    <span className={cx(colors.common, sizes[type][size], colors.styles[color].root, className)}>
      <Dot className={colors.styles[color].addon} size="sm" />
      {children}
    </span>
  )
}

interface BadgeWithIconProps<T extends BadgeTypes> {
    type?: T
    size?: Sizes
    color?: BadgeTypeToColorMap<typeof withBadgeTypes>[T]
    iconLeading?: IconComponentType
    iconTrailing?: IconComponentType
    children: ReactNode
    className?: string
}

export const BadgeWithIcon = <T extends BadgeTypes>(props: BadgeWithIconProps<T>) => {
  const { size = 'md', color = 'gray', type = 'pill-color', iconLeading: IconLeading, iconTrailing: IconTrailing, children, className } = props

  const colors = withBadgeTypes[type]

  const icon = IconLeading ? 'leading' : 'trailing'

  const pillSizes = {
    sm: {
      trailing: 'gap-0.5 py-0.5 pl-2 pr-1.5 text-xs font-regular',
      leading: 'gap-0.5 py-0.5 pr-2 pl-1.5 text-xs font-regular',
    },
    md: {
      trailing: 'gap-1 py-0.5 pl-2.5 pr-2 text-sm font-medium',
      leading: 'gap-1 py-0.5 pr-2.5 pl-2 text-sm font-medium',
    },
    lg: {
      trailing: 'gap-1 py-1 pl-3 pr-2.5 text-sm font-medium',
      leading: 'gap-1 py-1 pr-3 pl-2.5 text-sm font-medium',
    },
    xl: {
      trailing: 'gap-1.5 py-1.5 pl-4 pr-3.5 text-md font-medium',
      leading: 'gap-1.5 py-1.5 pr-4 pl-3.5 text-md font-medium',
    },
  }
  const badgeSizes = {
    sm: {
      trailing: 'gap-0.5 py-0.5 pl-2 pr-1.5 text-xs font-regular',
      leading: 'gap-0.5 py-0.5 pr-2 pl-1.5 text-xs font-regular',
    },
    md: {
      trailing: 'gap-1 py-0.5 pl-2 pr-1.5 text-sm font-medium',
      leading: 'gap-1 py-0.5 pr-2 pl-1.5 text-sm font-medium',
    },
    lg: {
      trailing: 'gap-1 py-1 pl-2.5 pr-2 text-sm font-medium rounded-lg',
      leading: 'gap-1 py-1 pr-2.5 pl-2 text-sm font-medium rounded-lg',
    },
    xl: {
      trailing: 'gap-1.5 py-1.5 pl-3.5 pr-3 text-md font-medium rounded-lg',
      leading: 'gap-1.5 py-1.5 pr-3.5 pl-3 text-md font-medium rounded-lg',
    },
  }

  const sizes = {
    [badgeTypes.pillColor]: pillSizes,
    [badgeTypes.badgeColor]: badgeSizes,
  }

  return (
    <span className={cx(colors.common, sizes[type][size][icon], colors.styles[color].root, className)}>
      {IconLeading && <IconLeading className={cx(colors.styles[color].addon, 'size-3 stroke-3')} />}
      {children}
      {IconTrailing && <IconTrailing className={cx(colors.styles[color].addon, 'size-3 stroke-3')} />}
    </span>
  )
}

interface BadgeWithFlagProps<T extends BadgeTypes> {
    type?: T
    size?: Sizes
    flag: FlagTypes
    color?: BadgeTypeToColorMap<typeof withPillTypes>[T]
    children: ReactNode
}

export const BadgeWithFlag = <T extends BadgeTypes>(props: BadgeWithFlagProps<T>) => {
  const { size = 'md', color = 'gray', flag, type = 'pill-color', children } = props
  const colors = withPillTypes[type]
  const pillSizes = {
    sm: 'gap-1 py-0.5 pl-0.75 pr-2 text-xs font-regular',
    md: 'gap-1.5 py-0.5 pl-1 pr-2.5 text-sm font-medium',
    lg: 'gap-1.5 py-1 pl-1.5 pr-3 text-sm font-medium',
    xl: 'gap-2 py-1.5 pl-2 pr-4 text-md font-medium shadow-xs-skeumorphic',
  }
  const badgeSizes = {
    sm: 'gap-1 py-0.5 pl-1 pr-1.5 text-xs font-regular',
    md: 'gap-1.5 py-0.5 pl-1.5 pr-2 text-sm font-medium',
    lg: 'gap-1.5 py-1 pl-2 pr-2.5 text-sm font-medium rounded-lg',
    xl: 'gap-2 py-1.5 pl-2.5 pr-3.5 text-md font-medium rounded-lg shadow-xs-skeumorphic',
  }
  const sizes = {
    [badgeTypes.pillColor]: pillSizes,
    [badgeTypes.badgeColor]: badgeSizes,
  }

  return (
    <span className={cx(colors.common, sizes[type][size], colors.styles[color].root)}>
      <Flag lang={flag} className="size-4 max-w-none rounded-full" />
      {children}
    </span>
  )
}

interface BadgeWithImageProps<T extends BadgeTypes> {
    type?: T
    size?: Sizes
    imgSrc: string
    color?: BadgeTypeToColorMap<typeof withPillTypes>[T]
    children: ReactNode
}

export const BadgeWithImage = <T extends BadgeTypes>(props: BadgeWithImageProps<T>) => {
  const { size = 'md', color = 'gray', type = 'pill-color', imgSrc, children } = props

  const colors = withPillTypes[type]

  const pillSizes = {
    sm: 'gap-1 py-0.5 pl-0.75 pr-2 text-xs font-regular',
    md: 'gap-1.5 py-0.5 pl-1 pr-2.5 text-sm font-medium',
    lg: 'gap-1.5 py-1 pl-1.5 pr-3 text-sm font-medium',
    xl: 'gap-2 py-1.5 pl-2 pr-4 text-md font-medium',
  }
  const badgeSizes = {
    sm: 'gap-1 py-0.5 pl-1 pr-1.5 text-xs font-regular',
    md: 'gap-1.5 py-0.5 pl-1.5 pr-2 text-sm font-medium',
    lg: 'gap-1.5 py-1 pl-2 pr-2.5 text-sm font-medium rounded-lg',
    xl: 'gap-2 py-1.5 pl-2.5 pr-3.5 text-md font-medium rounded-lg',
  }

  const sizes = {
    [badgeTypes.pillColor]: pillSizes,
    [badgeTypes.badgeColor]: badgeSizes,
  }

  return (
    <span className={cx(colors.common, sizes[type][size], colors.styles[color].root)}>
      <img src={imgSrc} className="size-4 max-w-none rounded-full" alt="Badge image" />
      {children}
    </span>
  )
}

interface BadgeWithButtonProps<T extends BadgeTypes> {
    type?: T
    size?: Sizes
    icon?: IconComponentType
    color?: BadgeTypeToColorMap<typeof withPillTypes>[T]
    children: ReactNode
    /**
     * The label for the button.
     */
    buttonLabel?: string
    /**
     * The click event handler for the button.
     */
    onButtonClick?: MouseEventHandler<HTMLButtonElement>
}

export const BadgeWithButton = <T extends BadgeTypes>(props: BadgeWithButtonProps<T>) => {
  const { size = 'md', color = 'gray', type = 'pill-color', icon: Icon = CloseX, buttonLabel, children } = props

  const colors = withPillTypes[type]

  const pillSizes = {
    sm: 'gap-0.5 py-0.5 pl-2 pr-0.75 text-xs font-regular',
    md: 'gap-0.5 py-0.5 pl-2.5 pr-1 text-sm font-medium',
    lg: 'gap-0.5 py-1 pl-3 pr-1.5 text-sm font-medium',
    xl: 'gap-0.5 py-1.5 pl-4 pr-2 text-md font-medium',
  }
  const badgeSizes = {
    sm: 'gap-0.5 py-0.5 pl-1.5 pr-0.75 text-xs font-regular',
    md: 'gap-0.5 py-0.5 pl-2 pr-1 text-sm font-medium',
    lg: 'gap-0.5 py-1 pl-2.5 pr-1.5 text-sm font-medium rounded-lg',
    xl: 'gap-0.5 py-1.5 pl-3.5 pr-2 text-md font-medium rounded-lg',
  }

  const sizes = {
    [badgeTypes.pillColor]: pillSizes,
    [badgeTypes.badgeColor]: badgeSizes,
  }

  return (
    <span className={cx(colors.common, sizes[type][size], colors.styles[color].root)}>
      {children}
      <button
        type="button"
        aria-label={buttonLabel}
        onClick={props.onButtonClick}
        className={cx(
          `
            flex cursor-pointer items-center justify-center p-0.5 outline-focus-ring transition duration-100 ease-linear
            focus-visible:outline-2
          `,
          colors.styles[color].addonButton,
          type === 'pill-color' ? 'rounded-full' : 'rounded-[3px]'
        )}
      >
        <Icon className="size-3 stroke-[3px] transition-inherit-all" />
      </button>
    </span>
  )
}

interface BadgeIconProps<T extends BadgeTypes> {
    type?: T
    size?: Sizes
    icon: IconComponentType
    color?: BadgeTypeToColorMap<typeof withPillTypes>[T]
    children?: ReactNode
}

export const BadgeIcon = <T extends BadgeTypes>(props: BadgeIconProps<T>) => {
  const { size = 'md', color = 'gray', type = 'pill-color', icon: Icon } = props

  const colors = withPillTypes[type]

  const pillSizes = {
    sm: 'p-1.25',
    md: 'p-1.5',
    lg: 'p-2',
    xl: 'p-2.5',
  }

  const badgeSizes = {
    sm: 'p-1.25',
    md: 'p-1.5',
    lg: 'p-2 rounded-lg',
    xl: 'p-2.5 rounded-lg',
  }

  const sizes = {
    [badgeTypes.pillColor]: pillSizes,
    [badgeTypes.badgeColor]: badgeSizes,
  }

  return (
    <span className={cx(colors.common, sizes[type][size], colors.styles[color].root)}>
      <Icon className={cx('size-3 stroke-[3px]', colors.styles[color].addon)} />
    </span>
  )
}
